/* global window */
/* eslint-disable no-param-reassign */

/*
 * This code was forked from https://github.com/lifeonairteam/rtcstats/commit/20b13acc1f7f898d5b506ded51d666a950c62ae4
 * There is also some development history in https://github.com/iknow/rtcstats
 */

import jsonpatch from 'fast-json-patch';

const isFirefox = !!window.mozRTCPeerConnection;
const isEdge = !!window.RTCIceGatherer;

// Utils

function deepCopyJSON(x) {
  return JSON.parse(JSON.stringify(x));
}

// transforms a maplike to an object. Mostly for getStats +
// JSON.parse(JSON.stringify())
function map2obj(m) {
  if (!m.entries) {
    return m;
  }
  const o = {};
  m.forEach(function (v, k) {
    o[k] = v;
  });
  return o;
}

function stripStatTimestamps(stats) {
  stats = deepCopyJSON(stats);
  Object.values(stats).forEach(function (report) {
    delete report.timestamp;
  });

  return stats;
}

// Inspection

function dumpTrack(track) {
  return {
    id: track.id,                 // unique identifier (GUID) for the track
    kind: track.kind,             // `audio` or `video`
    label: track.label,           // identified the track source
    enabled: track.enabled,       // application can control it
    muted: track.muted,           // application cannot control it (read-only)
    readyState: track.readyState, // `live` or `ended`
  };
}
function dumpStream(stream) {
  return {
    id: stream.id,
    tracks: stream.getTracks().map(dumpTrack),
  };
}

const inspectors = {
  MediaStream: dumpStream,

  RTCPeerConnectionIceEvent(e) {
    return e.candidate;
  },

  RTCTrackEvent(e) {
    return dumpTrack(e.track);
  },

  MediaStreamEvent(e) {
    return dumpStream(e.stream);
  },

  Object(o) {
    return o;
  },
};

function inspect(x) {
  if (x !== null && typeof x === 'object') {
    const typeName = x.constructor.name;
    const inspector = inspectors[typeName];
    return inspector ? inspector(x) : x;
  }
  return x;
}

// Method wrapping

function isThenable(x) {
  return x !== null && typeof x === 'object' && typeof x.then === 'function';
}

function traceMethod(trace, object, method, name) {
  const native = object[method];
  if (!native) return;
  if (!name) name = method;
  object[method] = function (..._arguments) {
    let args = Array.prototype.slice.call(_arguments).map(inspect);
    if (args.length === 0) {
      args = undefined;
    } else if (args.length === 1) {
      args = args[0];
    }
    trace(name, args);
    const returnValue = native.apply(this, _arguments);
    if (isThenable(returnValue)) {
      returnValue.then(
        function (value) { trace(`${name}OnSuccess`, inspect(value)); },
        function (failure) { trace(`${name}OnFailure`, inspect(failure)); },
      );
    }
    return returnValue;
  };
}

function traceEvent(trace, object, event, eventInspector) {
  eventInspector = eventInspector || inspect;
  const reportedEventName = `on${event}`;
  object.addEventListener(event, function (e) {
    trace(reportedEventName, eventInspector(e));
  });
}

function traceStateChangeEvent(trace, object, stateKey) {
  const changeEventName = `${stateKey.toLowerCase()}change`;
  const reportedEventName = `on${changeEventName}`;
  object.addEventListener(changeEventName, function () {
    trace(reportedEventName, object[stateKey]);
  });
}

// For regular browsers (firefox, chrome, safari, etc)
function getStatsNoSelector(pc) {
  return pc.getStats();
}

// For browsers that require selectors (ie, current edge (15),
// despite microsoft's documentation saying otherwise)
function getStatsWithTrackSelector(pc) {
  function getStreamStats(stream) {
    return stream.getTracks().map(function (track) {
      return pc.getStats(track);
    });
  }

  return Promise.all([].concat(
    Array.prototype.concat.apply([], pc.getLocalStreams().map(getStreamStats)),
    Array.prototype.concat.apply([], pc.getRemoteStreams().map(getStreamStats)),
  )).then(function (statsDicts) {
    if (statsDicts.length === 0) {
      return {};
    }

    return Object.assign.apply(null, statsDicts.map(map2obj));
  });
}

export function tracePeerConnection(pc, options) {
  let config = options.config || { nullConfig: true };
  const constraints = options.constraints;
  const trace = options.trace;
  const getStatsInterval = options.getStatsInterval;

  if (!config) {
    config = { nullConfig: true };
  }

  config = deepCopyJSON(config); // deepcopy
  // don't log credentials
  ((config && config.iceServers) || []).forEach(function (server) {
    delete server.credential;
  });

  if (isFirefox) {
    config.browserType = 'moz';
  } else if (isEdge) {
    config.browserType = 'edge';
  } else {
    config.browserType = 'webkit';
  }

  trace('create', config);

  // TODO: do we want to log constraints here? They are chrome-proprietary.
  // http://stackoverflow.com/questions/31003928/what-do-each-of-these-experimental-goog-rtcpeerconnectionconstraints-do
  if (constraints) {
    trace('constraints', constraints);
  }

  [
    'createDataChannel', 'close',
    'addTrack', 'removeTrack',
    'addStream', 'removeStream',
    'createOffer', 'createAnswer',
    'setLocalDescription', 'setRemoteDescription',
    'addIceCandidate',
  ].forEach(function (method) {
    traceMethod(trace, pc, method);
  });

  [
    'addstream', 'removestream',
    'track',
    'negotiationneeded',
    'datachannel',
  ].forEach(function (event) {
    traceEvent(trace, pc, event);
  });

  // Safari's RTCPeerConnectionIceEvent doesn't seem to have an appropriate constructor
  traceEvent(trace, pc, 'icecandidate', inspectors.RTCPeerConnectionIceEvent);

  [
    'connectionState',
    'signalingState',
    'iceConnectionState',
    'iceGatheringState',
  ].forEach(function (event) {
    traceStateChangeEvent(trace, pc, event);
  });

  // TODO: do we want one big interval and all peerconnections
  //    queried in that or one setInterval per PC?
  //    we have to collect results anyway so...

  const getStats = isEdge ? getStatsWithTrackSelector : getStatsNoSelector;

  let prev;
  const interval = setInterval(function () {
    if (pc.signalingState === 'closed') {
      clearInterval(interval);
      return;
    }
    getStats(pc).then(function (stats) {
      const now = stripStatTimestamps(map2obj(stats));

      if (prev === undefined) trace('getstats', now);
      else trace('statspatch', jsonpatch.compare(prev, now));

      prev = now;
    });
  }, getStatsInterval);

  return pc;
}
